import { createApp } from 'vue'
import { createHead } from '@vueuse/head'

import App from './App.vue'
import router from './router/index'
import { pinia } from '@/stores'
import 'splitpanes/dist/splitpanes.css'
import './assets/styles/main.css'
import i18n from './i18n'
import tooltip from './directives/tooltip.js'
import * as Sentry from '@sentry/vue'
import { initPlausibleAnalytics } from '@/services/analytics/plausible'

declare const __APP_VERSION__: string

const app = createApp(App)

const sentryEnabled = import.meta.env.VITE_SENTRY_ENABLED === 'true'
const sentryDsn = import.meta.env.VITE_SENTRY_DSN

if (sentryEnabled && sentryDsn) {
  Sentry.init({
    app,
    dsn: sentryDsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    release: `core-widget@${__APP_VERSION__}`,
    sendDefaultPii: true,
    enableLogs: true,
    normalizeDepth: 10,
    integrations: [Sentry.replayIntegration(), Sentry.browserTracingIntegration()],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  })
} else {
  console.info('[Sentry] Disabled - logging to console only')
}

app.use(pinia)
app.use(i18n)
app.use(router())
app.use(createHead())

app.directive('tooltip', tooltip)

initPlausibleAnalytics()

app.config.errorHandler = (err) => {
  /* handle error */
  /* ref: https://vuejs.org/error-reference/#runtime-errors */
  if (!sentryEnabled) {
    console.error('[Vue Error]', err)
  }
  Sentry.captureException(err)
}

app.mount('#app')

if (sentryEnabled) {
  Sentry.logger.info('Core Widget started', { version: __APP_VERSION__ })
} else {
  console.info('[Core Widget] Started', { version: __APP_VERSION__ })
}
